import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Sports } from '@gin-betting/constants';
import { App, View } from '@gin-betting/core';
import cn from 'classnames';

import globalMessages from 'messages';
import { MatchPartInfo } from 'types';
import { getMapNameFromMeta, getScoreTitle } from 'utils';
import useScore from './useScore';

import './Score.scss';

interface Props {
    isEnded?: boolean;
    numberOfShowed: number;
    hasMatchHeader?: boolean;
    sportId: string;
    matchId: string;
}

const Score: React.FC<Props> = ({
    isEnded,
    hasMatchHeader,
    numberOfShowed,
    sportId,
    matchId,
}) => {
    const {
        extra,
        showed,
        isServerKnown,
        isHomeServer,
        isAwayServer,
        winner,
        isExtendable,
        isFullView,
        toggleView,
        currentMatchPart,
        meta,
    } = useScore({ matchId, sportId, isEnded, numberOfShowed });

    const scoresFullLength = showed.length + extra.length;

    return (
        <div
            styleName={cn('container', {
                [`winner-${winner}`]: !!winner,
                'is-open': isFullView,
                'is-short-view': !isExtendable,
                'is-long': scoresFullLength > 8,
            })}
            className="__app-Score2-container"
        >
            {hasMatchHeader && (
                <div styleName="name" className="__app-Score2-name">
                    <FormattedMessage {...globalMessages.score} />
                </div>
            )}
            <div styleName="dynamic-scores-wrapper">
                <div
                    styleName={cn('dynamic-scores', {
                        'with-server': isServerKnown,
                    })}
                >
                    {isExtendable && (
                        <div styleName="toggle-wrapper" onClick={toggleView}>
                            <div styleName="toggle-btn" />
                        </div>
                    )}
                    {isServerKnown && (
                        <div styleName="score-server">
                            <div
                                styleName={cn('server', {
                                    active: isHomeServer,
                                })}
                            />
                            <div
                                styleName={cn('server', {
                                    active: isAwayServer,
                                })}
                            />
                        </div>
                    )}
                    {extra.map(score => (
                        <ScoreItem
                            key={score.type + score.indexNumber}
                            meta={meta}
                            score={score}
                            currentMatchPart={currentMatchPart}
                            sportId={sportId}
                        />
                    ))}
                </div>
            </div>
            <div styleName="constant-scores-wrapper ">
                {showed.map(score => (
                    <ScoreItem
                        key={score.type + score.indexNumber}
                        meta={meta}
                        score={score}
                        currentMatchPart={currentMatchPart}
                        sportId={sportId}
                    />
                ))}
            </div>
        </div>
    );
};

const ScoreItem: React.FC<{
    meta: View.Meta[];
    score: View.ScoreInfo;
    currentMatchPart: MatchPartInfo | null;
    sportId: string;
}> = ({ score, currentMatchPart, meta, sportId }) => {
    const isTotal = score.type === App.ScoreTypes.TYPE_TOTAL;

    const name = isTotal
        ? 'Total'
        : getScoreTitle({ score, currentMatchPart, meta });

    return (
        <div
            styleName={cn('score', {
                total: isTotal,
            })}
        >
            <div styleName="home">{score.home}</div>
            <div styleName="score-title">
                {sportId === Sports.ESPORTS_COUNTER_STRIKE &&
                score.type === App.ScoreTypes.TYPE_MAP
                    ? getMapNameFromMeta({
                          format: 'short',
                          map: name,
                      })
                    : name}
            </div>
            <div styleName="away">{score.away}</div>
        </div>
    );
};

export default Score;
